import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Input from '../../components/Input'
import SelectOption from '../../components/SelectOption'
import Button from '../../components/shared/Button'
import ButtonSubmit from '../../components/shared/ButtonSubmit'
import CheckBox from '../../components/shared/CheckBox'
import useFetch from '../../hooks/useFetch'
import { getProductById, updateProduct } from '../../utils/Apis/products'
import { getAllSubCategories } from '../../utils/Apis/subCategory'

import BulkPricing from '../../components/BulkPricing'
import { DeleteIcon } from '../../components/shared/Icons'
import {
  cn,
  convertToTitle,
  isObject,
  isPrimitiveDataType,
} from '../../utils/helperFunctions'
import { withErrorHandling } from '../../utils/withErrorHandling'
const fields = [
  'name',
  'Manufacturer',
  'CTYEA_Part_Number',
  'Manufacturer_Part_Number',
  'price',
  'description',
  'numOfReviews',
  'ratings',
  'stock',
  'multipleOrderQuantity',
  'Weight',
].map((item) => ({
  name: item,
  required: !['description', 'numOfReviews', 'ratings', 'multipleOrderQuantity'].includes(item),
}))

const hiddenProductAttributeKeys = new Set([
  'Spider_Source',
  'Spider_Source_Product_Code',
  'Spider_Source_Product_Id',
  'Spider_Source_Url',
  'Spider_Source_Image_Url',
  'Spider_Source_Category_Name',
  'Spider_Source_Category_Path',
  'TODO_Remark',
])

/**
 * 判断商品属性是否需要在编辑页展示。
 * @param {string} key 商品属性键名
 * @returns {boolean} 是否展示该属性
 */
const shouldShowProductAttribute = (key) => {
  return !hiddenProductAttributeKeys.has(key)
}

/**
 * 提取需要隐藏但提交时要保留的商品属性。
 * @param {Record<string, any>} productAttributes 商品属性对象
 * @returns {Record<string, any>} 隐藏属性对象
 */
const getHiddenProductAttributes = (productAttributes = {}) => {
  return Object.keys(productAttributes).reduce((hiddenAttributes, key) => {
    if (hiddenProductAttributeKeys.has(key)) {
      hiddenAttributes[key] = productAttributes[key]
    }

    return hiddenAttributes
  }, {})
}

const EditProduct = ({ className, ...props }) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: categories } = useFetch({
    fn: () => getAllSubCategories('', 1, 10000),
  })
  const { data: product } = useFetch({
    fn: () => getProductById(id),
  })

  // TODO: Fix this
  const [images, setImages] = useState()
  const [datasheet, setDatasheet] = useState(null)
  const [selectedCategory, setSelectedCategory] = React.useState('')
  const [attributes, setAttributes] = React.useState([])
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onBlur',
  })
  const onSubmit = withErrorHandling(async (data) => {
    const bulkPricing = document.querySelectorAll('.bulk_pricing')
    let bulkPricingData = Array.from(bulkPricing).map((item) => {
      const [qty, unitPrice] = item.querySelectorAll('.bulk_pricing input')
      return {
        Qty: qty.value,
        UnitPrice: unitPrice.value,
      }
    })
    bulkPricingData = bulkPricingData.filter(
      (item) => item.Qty && item.UnitPrice,
    )

    let attributes = document.querySelectorAll('.attributes')
    attributes = Array.from(attributes).map((attr) => {
      const input = attr.querySelector('.attributes input')
      const key = attr.querySelector('label').innerText
      const value = input.value
      return { key, value }
    })

    const convertAttributeToObj = (arr) => {
      return arr.reduce((acc, curr) => {
        acc[curr.key] = curr.value
        return acc
      }, {})
    }
    attributes = convertAttributeToObj(attributes)
    const formData = new FormData()
    Object.keys(data).forEach((key) => {
      if (
        key === 'sup_category' ||
        key === 'createdBy' ||
        key === '_id' ||
        key === 'category' ||
        !isPrimitiveDataType(data[key])
      )
        return
      formData.append(key, data[key])
    })
    if (images?.length > 0) {
      Object.keys(images).forEach((key) => {
        formData.append('image', images[key])
      })
    }
    if (datasheet) {
      formData.append('datasheet', datasheet)
    }
    if (isObject(attributes)) {
      const hiddenAttributes = getHiddenProductAttributes(
        product?.product?.product_Attributes || {},
      )
      formData.append(
        'product_Attributes',
        JSON.stringify({ ...attributes, ...hiddenAttributes }),
      )
    }
    if (selectedCategory) {
      const parentName = categories?.data?.find(
        (supCategory) => supCategory.name === selectedCategory,
      )?.parentCategory?.name
      formData.append(
        'sup_category',
        selectedCategory?.name || selectedCategory,
      )
      if (parentName) formData.append('category', parentName)
    }

    bulkPricingData.forEach((item, i) => {
      formData.append(`bulk_Pricing[${i}][Qty]`, item.Qty)
      formData.append(`bulk_Pricing[${i}][UnitPrice]`, item.UnitPrice)
    })

    const response = await updateProduct({
      id,
      payload: formData,
    })
    if (response?.product) {
      toast.success('Product Updated successfully')
      setTimeout(() => {
        navigate(`/products/view/${response.product._id}`)
      }, 2000)
    }
  })
  const handleOnChange = (e) => {
    if (e.target.files.length > 0) setImages(e.target.files)
  }

  /**
   * 处理 Datasheet 文件选择，仅保留单个 PDF 文件。
   * @param {React.ChangeEvent<HTMLInputElement>} e 文件选择事件
   */
  const handleDatasheetChange = (e) => {
    if (e.target.files.length > 0) {
      setDatasheet(e.target.files[0])
    }
  }
  useEffect(() => {
    if (categories?.data?.length > 0 && !selectedCategory) {
      setSelectedCategory(categories?.data[0].name)
    }
  }, [categories, selectedCategory])
  useEffect(() => {
    if (product?.product && categories?.data) {
      console.log('--- 属性合并排查 ---')
      console.log('1. 当前产品:', product.product)
      console.log('2. 所有分类数据:', categories.data)

      // 1. 获取当前产品所属分类的最新属性定义
      const currentCategoryName = selectedCategory || product.product.sup_category
      console.log('3. 确定的分类名称:', currentCategoryName)

      const currentCategory = categories.data.find(
        (cat) => cat.name === currentCategoryName,
      )
      console.log('4. 匹配到的分类对象:', currentCategory)

      const categoryAttributes = currentCategory?.attributes || []
      console.log('5. 分类定义的最新属性列表:', categoryAttributes)

      // 2. 获取产品已有的属性值
      const productAttrObj = product.product.product_Attributes || {}
      console.log('6. 产品数据库中现有的属性值:', productAttrObj)

      // 3. 合并：以分类定义的属性为准，填入产品已有的值
      const mergedAttributes = categoryAttributes.map((attr) => {
        return {
          name: attr.name,
          value: productAttrObj[attr.name] || '',
        }
      })
      console.log('7. 基于分类合并后的属性(中间结果):', mergedAttributes)

      // 4. 处理那些产品有但分类里可能已经删掉的属性，保持数据完整性
      Object.keys(productAttrObj).forEach((key) => {
        if (
          shouldShowProductAttribute(key) &&
          !mergedAttributes.find((a) => a.name === key)
        ) {
          mergedAttributes.push({ name: key, value: productAttrObj[key] })
        }
      })
      console.log('8. 最终合并后的属性列表(attributes 状态):', mergedAttributes)
      console.log('--------------------')

      setAttributes(mergedAttributes)
      reset(product.product)
      if (product.product.sup_category && !selectedCategory) {
        setSelectedCategory(product.product.sup_category)
      }
    }
  }, [product, categories, selectedCategory, reset])
  // console.log("Attrbutes",attributes)
  return (
    <div
      className={cn(
        ' custom-scroll flex h-full w-full flex-col items-end gap-6 overflow-auto px-2',
        className,
      )}
      {...props}
    >
      <Button>
        <NavLink to={'/products'}>All Products</NavLink>
      </Button>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex w-full flex-col items-center gap-8 lg:gap-6 lg:pl-[6rem]"
      >
        {fields.map((field, index) => {
          return (
            <Input
              key={field.name}
              register={register(field.name, {
                required: field.required,
              })}
              errorMsg={
                Object.keys(errors).includes(field.name)
                  ? errors[field.name].message
                  : ''
              }
              label={
                field.name.toLowerCase() === 'weight'
                  ? `${convertToTitle(field.name)} gram(s)`
                  : convertToTitle(field.name)
              }
              required={field.required}
              className="w-full"
            />
          )
        })}
        <SelectOption
          label={convertToTitle('manufacturerLeadTimeUnit')}
          register={register('manufacturerLeadTimeUnit')}
          options={[
            { name: 'Days', value: 'Days' },
            { name: 'Weeks', value: 'Weeks' },
            { name: 'Months', value: 'Months' },
            { name: 'Years', value: 'Years' },
          ]}
        />
        <Input
          label={'Image'}
          type="file"
          onChange={handleOnChange}
          multiple
          required={false}
        />
        <Input
          label={'Datasheet(PDF)'}
          type="file"
          onChange={handleDatasheetChange}
          accept="application/pdf"
          required={false}
        />
        {product?.product?.datasheet && (
          <div className="flex w-full justify-start lg:justify-end">
            <div className="w-full lg:w-3/4">
              <a
                href={product.product.datasheet}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-blue-600 underline"
              >
                View current datasheet
              </a>
            </div>
          </div>
        )}

        {categories?.data && categories?.data?.length > 0 && (
          <SelectOption
            label="Category"
            options={categories?.data?.map((category) => ({
              value: category.name,
              name: category.name,
            }))}
            register={register('sup_category')}
            onChange={(e) => setSelectedCategory(e.target.value)}
          />
        )}
        <div className="flex w-full items-start">
          <CheckBox
            register={register('IsQuote')}
            label="Is Quote"
            id={'IsQuote'}
          />
        </div>
        <div className="flex w-full flex-col items-start gap-3">
          <h3 className="font-bold">Product Attributes</h3>
          <ul className="flex w-full flex-col gap-3 lg:w-[40%]">
            {attributes?.map((attr, index) => {
              return (
                <li
                  key={index}
                  className="attributes flex w-full flex-col items-start gap-2"
                >
                  <div className="flex w-full items-center justify-between">
                    <label htmlFor={attr?.name}>{attr.name}</label>
                    <button
                      type="button"
                      onClick={() => {
                        const newAttributes = [...attributes]
                        newAttributes.splice(index, 1)
                        setAttributes(newAttributes)
                      }}
                      className="text-destructive hover:text-red-700"
                      title="Delete Attribute"
                    >
                      <DeleteIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <input
                    id={attr?.name}
                    type="text"
                    className="w-full rounded-lg border p-2"
                    defaultValue={attr.value}
                  />
                </li>
              )
            })}
          </ul>
        </div>
        {product?.product?.bulk_Pricing && (
          <BulkPricing
            number={product?.product?.bulk_Pricing?.length}
            initialData={product?.product?.bulk_Pricing}
          />
        )}
        <div className="flex flex-col items-center gap-3">
          <div className="flex flex-col items-center gap-2 md:flex-row">
            <ButtonSubmit disabled={!isValid} type="submit">
              Update Product
            </ButtonSubmit>
          </div>
        </div>
      </form>
    </div>
  )
}

export default EditProduct
